import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const STATUS_COLOR = { uploaded: '#e0af68', processing: '#7aa2f7', ready: '#9ece6a', error: '#f7768e' };

function formatSize(b) { if (!b) return '-'; const k = 1024; const s = ['B','KB','MB','GB']; const i = Math.floor(Math.log(b)/Math.log(k)); return (b/Math.pow(k,i)).toFixed(1)+' '+s[i]; }

export default function DocDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`/api/documents/${id}`).then(r => r.ok ? r.json() : null).then(d => { setDoc(d?.id ? d : null); setLoading(false); }).catch(() => setLoading(false));
  }, [id]);

  const loadContent = async () => {
    try {
      const r = await fetch(`/api/workspace/${id}/extract`);
      const d = await r.json();
      setEditContent(d.text || d.content || '');
      setEditMode(true);
    } catch {}
  };

  const saveContent = async () => {
    setSaving(true);
    await fetch(`/api/workspace/${id}/edit`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ content: editContent }) }).catch(() => {});
    setSaving(false);
  };

  const handleCopy = async () => {
    try {
      const r = await fetch(`/api/workspace/${id}/copy`, { method: 'POST' });
      const d = await r.json();
      if (d.id) nav(`/library/${d.id}`);
    } catch {}
  };

  if (loading) return <div style={{ padding: 'var(--gap-lg)', color: 'var(--text-muted)' }}>Laden...</div>;
  if (!doc) return <div style={{ padding: 'var(--gap-lg)', color: '#f7768e' }}>Dokument nicht gefunden</div>;

  return (
    <div style={{ display: 'flex', gap: 'var(--gap-lg)', padding: 'var(--gap-lg)', height: '100%', overflow: 'auto' }}>
      {/* Main */}
      <div style={{ flex: 2, minWidth: 0 }}>
        <button onClick={() => nav('/library')} style={{ background: 'none', border: 'none', color: '#bb9af7', cursor: 'pointer', marginBottom: 12, fontSize: 'var(--font-size-sm)' }}>{'\u2190'} Bibliothek</button>
        <h2 style={{ color: 'var(--text-primary)', marginBottom: 'var(--gap-md)', fontSize: 'var(--font-size-xl)', fontWeight: 600, wordBreak: 'break-word' }}>{doc.title || doc.filename}</h2>

        {editMode ? (
          <div>
            <textarea value={editContent} onChange={e => setEditContent(e.target.value)} style={{ width: '100%', minHeight: 400, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius)', padding: 16, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-sm)', resize: 'vertical', outline: 'none' }} />
            <button onClick={saveContent} disabled={saving} style={{ marginTop: 8, background: '#bb9af7', border: 'none', borderRadius: 'var(--border-radius)', padding: '8px 16px', color: '#1a1b26', fontWeight: 600, cursor: 'pointer' }}>
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        ) : doc.file_type === 'pdf' ? (
          <iframe src={`/api/documents/${id}/pdf`} style={{ width: '100%', height: 'calc(100vh - 200px)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius)' }} />
        ) : (
          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius)', padding: 24, color: 'var(--text-secondary)', minHeight: 200 }}>
            Vorschau nicht verfuegbar fuer {doc.file_type}
          </div>
        )}
      </div>

      {/* Sidebar */}
      <div style={{ width: 280, minWidth: 280 }}>
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius-lg)', padding: 'var(--gap-md)' }}>
          <h3 style={{ color: 'var(--text-primary)', fontSize: 'var(--font-size-base)', fontWeight: 600, marginBottom: 12 }}>Metadaten</h3>
          {[
            ['Datei', doc.filename],
            ['Typ', doc.file_type?.toUpperCase()],
            ['Groesse', formatSize(doc.file_size)],
            ['Seiten', doc.page_count || '-'],
            ['Sprache', doc.language || '-'],
            ['Status', doc.status],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
              <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>{k}</span>
              <span style={{ color: k === 'Status' ? STATUS_COLOR[v] : 'var(--text-primary)', fontSize: 'var(--font-size-sm)', fontWeight: k === 'Status' ? 600 : 400 }}>{v}</span>
            </div>
          ))}

          {doc.tags?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)', marginBottom: 6 }}>Tags</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {doc.tags.map(t => <span key={t} style={{ background: 'var(--bg-tertiary)', borderRadius: 4, padding: '2px 8px', fontSize: 11, color: 'var(--text-secondary)' }}>{t}</span>)}
              </div>
            </div>
          )}

          {doc.summary && (
            <div style={{ marginTop: 12 }}>
              <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)', marginBottom: 6 }}>Zusammenfassung</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)', lineHeight: 1.5 }}>{doc.summary}</div>
            </div>
          )}

          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button onClick={() => nav(`/analyze?doc=${id}`)} style={{ background: 'rgba(187,154,247,0.15)', border: '1px solid rgba(187,154,247,0.3)', borderRadius: 'var(--border-radius)', padding: '8px 12px', color: '#bb9af7', cursor: 'pointer', fontSize: 'var(--font-size-sm)', textAlign: 'left' }}>{'\uD83E\uDDE0'} KI-Analyse</button>
            {doc.status === 'ready' && <button onClick={() => nav(`/chat?doc=${id}`)} style={{ background: 'rgba(122,162,247,0.15)', border: '1px solid rgba(122,162,247,0.3)', borderRadius: 'var(--border-radius)', padding: '8px 12px', color: '#7aa2f7', cursor: 'pointer', fontSize: 'var(--font-size-sm)', textAlign: 'left' }}>{'\uD83D\uDCAC'} Chat</button>}
            <button onClick={loadContent} style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius)', padding: '8px 12px', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 'var(--font-size-sm)', textAlign: 'left' }}>{'\u270F\uFE0F'} Bearbeiten</button>
            <button onClick={handleCopy} style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius)', padding: '8px 12px', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 'var(--font-size-sm)', textAlign: 'left' }}>{'\uD83D\uDCCB'} Kopie erstellen</button>
            <button onClick={() => window.open(`/api/documents/${id}/download`)} style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius)', padding: '8px 12px', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 'var(--font-size-sm)', textAlign: 'left' }}>{'\u2B07\uFE0F'} Download</button>
          </div>
        </div>
      </div>
    </div>
  );
}
